import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  AlarmClock, ArrowRightLeft, BadgeIndianRupee, Ban, Building2, CalendarClock, CalendarDays,
  CheckCircle2, ChevronLeft, ChevronRight, CircleAlert, ClipboardList, History, ListTodo,
  Loader2, Lock, Pencil, Play, Plus, RefreshCw, Settings2, ShieldCheck, TrendingUp, UserRound, X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { KpiCard } from "@/components/common/KpiCard";
import { EmptyState } from "@/components/common/EmptyState";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, formatDateTime } from "@/lib/constants";

const todayIso = () => new Date().toISOString().slice(0, 10);

const VIEW_META = {
  admin: { title: "Work Queue", subtitle: "Every operational action across all departments — organized automatically." },
  recruiter: { title: "My Work Queue", subtitle: "Today's calls, interviews, joinings and follow-ups — no searching needed." },
  backoffice: { title: "Back Office Work Queue", subtitle: "Lineup mails, selection verifications and interview results. Admin-operated until the Back Office role is activated." },
  accounts: { title: "Accounts Work Queue", subtitle: "Billing reviews and payment follow-ups. Admin-operated until the Accounts role is activated." },
};

const PRIORITY_STYLES = {
  Critical: "bg-red-50 text-red-700 border-red-200",
  High: "bg-amber-50 text-amber-700 border-amber-200",
  Normal: "bg-sky-50 text-sky-700 border-sky-200",
  Low: "bg-slate-100 text-slate-600 border-slate-200",
};

const PriorityBadge = ({ value }) => (
  <Badge variant="outline" className={`rounded-full ${PRIORITY_STYLES[value] || PRIORITY_STYLES.Normal}`} data-testid="task-priority-badge">
    {value}
  </Badge>
);

const StatusPill = ({ t }) => {
  if (t.status === "Completed")
    return <Badge variant="outline" className="rounded-full bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="task-status-badge">{t.auto_closed ? "Auto-closed" : "Completed"}</Badge>;
  if (t.status === "Cancelled")
    return <Badge variant="outline" className="rounded-full bg-slate-100 text-slate-500 border-slate-200" data-testid="task-status-badge">Cancelled</Badge>;
  if (t.status === "In Progress")
    return <Badge variant="outline" className="rounded-full bg-violet-50 text-violet-700 border-violet-200" data-testid="task-status-badge">In Progress</Badge>;
  if (t.overdue || (t.due_date && t.due_date < todayIso()))
    return <Badge variant="outline" className="rounded-full bg-red-50 text-red-700 border-red-200" data-testid="task-status-badge">Overdue</Badge>;
  if (t.due_date === todayIso())
    return <Badge variant="outline" className="rounded-full bg-amber-50 text-amber-700 border-amber-200" data-testid="task-status-badge">Due Today</Badge>;
  return <Badge variant="outline" className="rounded-full bg-sky-50 text-sky-700 border-sky-200" data-testid="task-status-badge">Pending</Badge>;
};

const SourceBadge = ({ value }) => (
  <Badge variant="outline" className={`rounded-full ${value === "Manual" ? "bg-violet-50 text-violet-700 border-violet-200" : "bg-slate-50 text-slate-600 border-slate-200"}`} data-testid="task-source-badge">
    {value === "Manual" ? "Manual" : "System"}
  </Badge>
);

const refCode = (t) =>
  t.fup_code || t.lineup_code || t.interview_code || t.emp_code || t.bill_code || t.fwd_code || t.app_code || "—";

const EMPTY_FILTERS = { task_type: "all", priority: "all", source: "all", department: "all", recruiter_id: "all", status: "all" };

export default function WorkQueue({ view = "recruiter" }) {
  const { user } = useAuth();
  const isAdmin = user.role === "admin" || user.role === "super_admin";
  const meta = VIEW_META[view] || VIEW_META.recruiter;
  const deptView = view === "backoffice" || view === "accounts";

  const [kpis, setKpis] = useState(null);
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState("today");
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState("due_asc");
  const [page, setPage] = useState(1);
  const [options, setOptions] = useState({ task_types: [], statuses: [], priorities: [], departments: [], sources: [], assignees: [] });
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ task_type: "", title: "", description: "", due_date: todayIso(), priority: "", assigned_user_id: "" });

  const [action, setAction] = useState(null); // {mode:'complete'|'cancel'|'reassign'|'edit', t}
  const [actionForm, setActionForm] = useState({});

  const [historyOf, setHistoryOf] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Reports + settings (admin view)
  const [reports, setReports] = useState(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [lockingCfg, setLockingCfg] = useState(null);
  const [cfgEdit, setCfgEdit] = useState(null); // {company_id, company_name, value}

  const viewParam = deptView ? view : undefined;

  useEffect(() => {
    api.get("/work-queue/options").then((res) => setOptions(res.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (tab === "reports" || tab === "settings") return;
    setError(false);
    const params = { page, limit: 25, sort };
    if (viewParam) params.view = viewParam;
    if (["today", "overdue", "upcoming", "in_progress", "completed"].includes(tab)) params.bucket = tab;
    if (q.trim()) params.q = q.trim();
    if (filters.task_type !== "all") params.task_type = filters.task_type;
    if (filters.priority !== "all") params.priority = filters.priority;
    if (filters.source !== "all") params.source = filters.source;
    if (tab === "all" && filters.status !== "all") params.status = filters.status;
    if (isAdmin && view === "admin") {
      if (filters.department !== "all") params.department = filters.department;
      if (filters.recruiter_id !== "all") params.recruiter_id = filters.recruiter_id;
    }
    const kpiParams = viewParam ? { view: viewParam } : {};
    if (isAdmin && view === "admin" && filters.recruiter_id !== "all") kpiParams.recruiter_id = filters.recruiter_id;
    Promise.all([api.get("/work-queue", { params }), api.get("/work-queue/kpis", { params: kpiParams })])
      .then(([l, k]) => { setData(l.data); setKpis(k.data); setSelected([]); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [tab, q, filters, sort, page, viewParam, isAdmin, view]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setPage(1); }, [tab, q, filters, sort]);

  const loadReports = useCallback(() => {
    setReportsLoading(true);
    api.get("/work-queue/reports").then((res) => setReports(res.data)).catch((e) => toast.error(getErrorMessage(e)))
      .finally(() => setReportsLoading(false));
  }, []);
  const loadLocking = useCallback(() => {
    api.get("/work-queue/locking-configs").then((res) => setLockingCfg(res.data)).catch((e) => toast.error(getErrorMessage(e)));
  }, []);
  useEffect(() => {
    if (tab === "reports") loadReports();
    if (tab === "settings") loadLocking();
  }, [tab, loadReports, loadLocking]);

  const filtersActive = q || Object.entries(filters).some(([, v]) => v !== "all");

  const openCreate = () => {
    setCreateForm({ task_type: "", title: "", description: "", due_date: todayIso(), priority: "", assigned_user_id: isAdmin ? "" : user.id });
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    if (!createForm.task_type) return toast.error("Choose a task type");
    if (!createForm.title || createForm.title.trim().length < 3) return toast.error("Enter a task title (min 3 characters)");
    if (!createForm.due_date) return toast.error("Due date is required");
    setBusy(true);
    try {
      const payload = {
        task_type: createForm.task_type, title: createForm.title.trim(),
        description: createForm.description || null, due_date: createForm.due_date,
        priority: createForm.priority || null,
      };
      if (isAdmin && createForm.assigned_user_id) payload.assigned_user_id = createForm.assigned_user_id;
      const res = await api.post("/work-queue", payload);
      toast.success(`Task ${res.data.task_code} created`);
      setCreateOpen(false);
      load();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(false); }
  };

  const startTask = async (t) => {
    try {
      await api.put(`/work-queue/${t.id}/status`, { action: "start" });
      toast.success(`${t.task_code} started`);
      load();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const submitAction = async () => {
    const { mode, t } = action;
    setBusy(true);
    try {
      if (mode === "complete") {
        await api.put(`/work-queue/${t.id}/status`, { action: "complete", notes: actionForm.notes || null });
        toast.success(`${t.task_code} completed`);
      } else if (mode === "cancel") {
        if (!actionForm.reason || actionForm.reason.trim().length < 3) { setBusy(false); return toast.error("A cancellation reason is mandatory"); }
        await api.post(`/work-queue/${t.id}/cancel`, { reason: actionForm.reason.trim() });
        toast.success(`${t.task_code} cancelled`);
      } else if (mode === "reassign") {
        if (!actionForm.user_id) { setBusy(false); return toast.error("Choose the new assignee"); }
        await api.put(`/work-queue/${t.id}/reassign`, { user_id: actionForm.user_id, remarks: actionForm.remarks || null });
        toast.success(`${t.task_code} reassigned`);
      } else if (mode === "edit") {
        await api.put(`/work-queue/${t.id}`, {
          title: actionForm.title || null, due_date: actionForm.due_date || null,
          priority: actionForm.priority || null, description: actionForm.description ?? null,
        });
        toast.success(`${t.task_code} updated`);
      }
      setAction(null);
      load();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(false); }
  };

  const bulk = async (mode) => {
    if (!selected.length) return;
    let reason = null;
    if (mode === "cancel") {
      reason = window.prompt(`Cancellation reason for ${selected.length} task(s):`);
      if (!reason || reason.trim().length < 3) return toast.error("A cancellation reason is mandatory");
    }
    setBusy(true);
    try {
      const res = await api.post("/work-queue/bulk", { action: mode, ids: selected, reason });
      toast.success(`${res.data.count} task(s) ${mode === "cancel" ? "cancelled" : "completed"}`);
      load();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(false); }
  };

  const openHistory = async (t) => {
    setHistoryOf(t);
    setHistoryLoading(true);
    try {
      const res = await api.get(`/work-queue/${t.id}/history`);
      setHistory(res.data || []);
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setHistoryLoading(false); }
  };

  const saveLockingCfg = async () => {
    const raw = (cfgEdit.value || "").split(",").map((s) => s.trim()).filter(Boolean);
    if (raw.some((s) => !/^\d+$/.test(s))) return toast.error("Enter day numbers separated by commas, e.g. 20, 40, 60");
    setBusy(true);
    try {
      await api.put(`/work-queue/locking-configs/${cfgEdit.company_id}`, { days: raw.map(Number) });
      toast.success(`Locking schedule updated for ${cfgEdit.company_name}`);
      setCfgEdit(null);
      loadLocking();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(false); }
  };

  const canManage = (t) => isAdmin || t.assigned_user_id === user.id;
  const canEdit = (t) => isAdmin || (t.assigned_user_id === user.id && t.task_source === "Manual");
  const openItems = useMemo(() => data.items.filter((t) => t.status === "Pending" || t.status === "In Progress"), [data.items]);
  const allSelected = openItems.length > 0 && selected.length === openItems.length;

  const typeSummary = useMemo(() => Object.entries(kpis?.open_by_type || {}).sort((a, b) => b[1].total - a[1].total), [kpis]);

  return (
    <div className="space-y-6" data-testid={`work-queue-page-${view}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold" data-testid="work-queue-title">{meta.title}</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{meta.subtitle}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && view === "admin" && (
            <>
              <Button variant="secondary" size="sm" className="rounded-lg" asChild data-testid="open-backoffice-queue-link">
                <Link to="/backoffice/work-queue"><Building2 className="h-4 w-4 mr-1.5" />Back Office</Link>
              </Button>
              <Button variant="secondary" size="sm" className="rounded-lg" asChild data-testid="open-accounts-queue-link">
                <Link to="/accounts/work-queue"><BadgeIndianRupee className="h-4 w-4 mr-1.5" />Accounts</Link>
              </Button>
            </>
          )}
          {deptView && (
            <Button variant="secondary" size="sm" className="rounded-lg" asChild data-testid="back-to-admin-queue-link">
              <Link to="/admin/work-queue"><ListTodo className="h-4 w-4 mr-1.5" />All work</Link>
            </Button>
          )}
          {!deptView && (
            <Button onClick={openCreate} className="rounded-lg" data-testid="create-task-button">
              <Plus className="h-4 w-4 mr-1.5" /> New task
            </Button>
          )}
        </div>
      </div>

      {deptView && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2" data-testid="dept-deferred-note">
          <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
          <span>This queue is fully operational and currently managed by Admin. When the dedicated {view === "backoffice" ? "Back Office" : "Accounts"} role is activated, only the access mapping changes — no workflow changes.</span>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <KpiCard label="Due today" value={kpis ? kpis.due_today : "—"} icon={CalendarClock} tone="primary" testId="kpi-due-today" />
        <KpiCard label="Overdue" value={kpis ? kpis.overdue : "—"} icon={CircleAlert} tone="warning" testId="kpi-overdue" />
        <KpiCard label="Upcoming" value={kpis ? kpis.upcoming : "—"} icon={CalendarDays} tone="slate" testId="kpi-upcoming" />
        <KpiCard label="In progress" value={kpis ? kpis.in_progress : "—"} icon={Play} tone="violet" testId="kpi-in-progress" />
        <KpiCard label="Completed" value={kpis ? kpis.completed : "—"} icon={CheckCircle2} tone="success" testId="kpi-completed" />
      </div>

      {/* Open work by type */}
      {typeSummary.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="open-by-type-chips">
          {typeSummary.map(([type, c]) => (
            <button
              key={type}
              onClick={() => { setFilters((f) => ({ ...f, task_type: f.task_type === type ? "all" : type })); setTab("all"); }}
              data-testid={`type-chip-${type.toLowerCase().replace(/\s+/g, "-")}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                filters.task_type === type ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-muted border-border text-foreground"
              }`}
            >
              {type}
              <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-mono ${filters.task_type === type ? "bg-white/20" : "bg-muted"}`}>{c.total}</span>
              {c.overdue > 0 && <span className="rounded-full bg-red-100 text-red-700 px-1.5 py-0.5 text-[10px] font-mono">{c.overdue} late</span>}
            </button>
          ))}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto gap-1 rounded-xl" data-testid="work-queue-tabs">
          <TabsTrigger value="today" className="rounded-lg" data-testid="tab-today">Today</TabsTrigger>
          <TabsTrigger value="overdue" className="rounded-lg" data-testid="tab-overdue">Overdue</TabsTrigger>
          <TabsTrigger value="upcoming" className="rounded-lg" data-testid="tab-upcoming">Upcoming</TabsTrigger>
          <TabsTrigger value="in_progress" className="rounded-lg" data-testid="tab-in-progress">In progress</TabsTrigger>
          <TabsTrigger value="completed" className="rounded-lg" data-testid="tab-completed">Completed</TabsTrigger>
          <TabsTrigger value="all" className="rounded-lg" data-testid="tab-all">All</TabsTrigger>
          {isAdmin && view === "admin" && (
            <>
              <TabsTrigger value="reports" className="rounded-lg" data-testid="tab-reports"><TrendingUp className="h-3.5 w-3.5 mr-1" />Reports</TabsTrigger>
              <TabsTrigger value="settings" className="rounded-lg" data-testid="tab-settings"><Settings2 className="h-3.5 w-3.5 mr-1" />Settings</TabsTrigger>
            </>
          )}
        </TabsList>
      </Tabs>

      {tab === "reports" && isAdmin ? (
        <ReportsPanel reports={reports} loading={reportsLoading} onRefresh={loadReports} />
      ) : tab === "settings" && isAdmin ? (
        <LockingSettings cfg={lockingCfg} onEdit={(row) => setCfgEdit({ company_id: row.company_id, company_name: row.company_name, value: (row.days || []).join(", ") })} />
      ) : (
        <>
          {/* Toolbar */}
          <Card className="rounded-xl border-border shadow-none">
            <CardContent className="p-4 flex flex-wrap items-center gap-2">
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search task ID, candidate, company, reference…"
                className="h-9 rounded-md w-full sm:w-72"
                data-testid="work-queue-search-input"
              />
              <Select value={filters.task_type} onValueChange={(v) => setFilters((f) => ({ ...f, task_type: v }))}>
                <SelectTrigger className="h-9 w-[170px] rounded-md" data-testid="filter-task-type"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {options.task_types.map((t) => <SelectItem key={t.value} value={t.value}>{t.value}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.priority} onValueChange={(v) => setFilters((f) => ({ ...f, priority: v }))}>
                <SelectTrigger className="h-9 w-[130px] rounded-md" data-testid="filter-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All priorities</SelectItem>
                  {options.priorities.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.source} onValueChange={(v) => setFilters((f) => ({ ...f, source: v }))}>
                <SelectTrigger className="h-9 w-[125px] rounded-md" data-testid="filter-source"><SelectValue placeholder="Source" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sources</SelectItem>
                  {options.sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              {tab === "all" && (
                <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
                  <SelectTrigger className="h-9 w-[135px] rounded-md" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {options.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {isAdmin && view === "admin" && (
                <>
                  <Select value={filters.department} onValueChange={(v) => setFilters((f) => ({ ...f, department: v }))}>
                    <SelectTrigger className="h-9 w-[145px] rounded-md" data-testid="filter-department"><SelectValue placeholder="Department" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All departments</SelectItem>
                      {options.departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={filters.recruiter_id} onValueChange={(v) => setFilters((f) => ({ ...f, recruiter_id: v }))}>
                    <SelectTrigger className="h-9 w-[160px] rounded-md" data-testid="filter-assignee"><SelectValue placeholder="Assignee" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All assignees</SelectItem>
                      {options.assignees.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </>
              )}
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="h-9 w-[150px] rounded-md" data-testid="sort-select"><SelectValue placeholder="Sort" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="due_asc">Due date ↑</SelectItem>
                  <SelectItem value="due_desc">Due date ↓</SelectItem>
                  <SelectItem value="priority">Priority</SelectItem>
                  <SelectItem value="created_desc">Newest first</SelectItem>
                  <SelectItem value="created_asc">Oldest first</SelectItem>
                </SelectContent>
              </Select>
              {filtersActive && (
                <Button variant="ghost" size="sm" className="rounded-lg text-muted-foreground" onClick={() => { setQ(""); setFilters(EMPTY_FILTERS); }} data-testid="clear-filters-button">
                  <X className="h-4 w-4 mr-1" /> Clear
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Bulk bar */}
          {isAdmin && selected.length > 0 && (
            <div className="flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-2.5" data-testid="bulk-actions-bar">
              <p className="text-sm font-medium">{selected.length} selected</p>
              <Button size="sm" variant="secondary" className="rounded-lg" onClick={() => bulk("complete")} disabled={busy} data-testid="bulk-complete-button">
                <CheckCircle2 className="h-4 w-4 mr-1.5" /> Complete
              </Button>
              <Button size="sm" variant="secondary" className="rounded-lg text-destructive" onClick={() => bulk("cancel")} disabled={busy} data-testid="bulk-cancel-button">
                <Ban className="h-4 w-4 mr-1.5" /> Cancel
              </Button>
              <Button size="sm" variant="ghost" className="rounded-lg ml-auto" onClick={() => setSelected([])} data-testid="bulk-clear-selection">Clear</Button>
            </div>
          )}

          {/* List */}
          {loading ? (
            <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 rounded-xl" />)}</div>
          ) : error ? (
            <EmptyState icon={CircleAlert} title="Could not load the work queue" description="Please check your connection and try again."
              actionLabel="Retry" onAction={load} testId="work-queue-error-state" />
          ) : data.items.length === 0 ? (
            <EmptyState icon={ListTodo} title="No work here — you're all caught up"
              description={tab === "today" ? "Nothing is due today. New work appears automatically as operations progress." : "No tasks match the current view or filters."}
              testId="work-queue-empty-state" />
          ) : (
            <>
              {/* Desktop table */}
              <Card className="rounded-xl border-border shadow-none hidden md:block">
                <Table data-testid="work-queue-table">
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      {isAdmin && (
                        <TableHead className="w-10">
                          <Checkbox checked={allSelected} onCheckedChange={(v) => setSelected(v ? openItems.map((t) => t.id) : [])} data-testid="bulk-select-all" aria-label="Select all" />
                        </TableHead>
                      )}
                      <TableHead>Task</TableHead>
                      <TableHead>Work item</TableHead>
                      <TableHead>Due</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Assigned to</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-12" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.items.map((t) => (
                      <TaskRow key={t.id} t={t} isAdmin={isAdmin} selected={selected} setSelected={setSelected}
                        canManage={canManage} canEdit={canEdit} onStart={startTask} onAction={(mode) => { setAction({ mode, t }); setActionForm(mode === "edit" ? { title: t.title, due_date: t.due_date, priority: t.priority, description: t.description || "" } : {}); }}
                        onHistory={openHistory} />
                    ))}
                  </TableBody>
                </Table>
              </Card>

              {/* Mobile cards */}
              <div className="md:hidden space-y-3" data-testid="work-queue-cards">
                {data.items.map((t) => (
                  <Card key={t.id} className="rounded-xl border-border shadow-none" data-testid={`task-card-${t.task_code}`}>
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-xs text-muted-foreground">{t.task_code}</span>
                        <StatusPill t={t} />
                      </div>
                      <p className="text-sm font-medium">{t.title}</p>
                      <div className="flex flex-wrap gap-1.5">
                        <Badge variant="outline" className="rounded-full">{t.task_type}</Badge>
                        <PriorityBadge value={t.priority} />
                        <SourceBadge value={t.task_source} />
                      </div>
                      <div className="text-xs text-muted-foreground space-y-0.5">
                        <p className={t.overdue ? "text-red-600 font-medium" : ""}>Due {formatDate(t.due_date)}</p>
                        <p>{t.candidate_name || refCode(t)} {t.company_name ? `· ${t.company_name}` : ""}</p>
                        <p className="flex items-center gap-1"><UserRound className="h-3 w-3" />{t.assigned_user_name || "—"}</p>
                      </div>
                      <div className="flex flex-wrap gap-2 pt-1">
                        {(t.status === "Pending") && canManage(t) && (
                          <Button size="sm" variant="secondary" className="rounded-lg" onClick={() => startTask(t)} data-testid={`start-task-${t.task_code}`}><Play className="h-3.5 w-3.5 mr-1" />Start</Button>
                        )}
                        {(t.status === "Pending" || t.status === "In Progress") && canManage(t) && (
                          <Button size="sm" className="rounded-lg" onClick={() => { setAction({ mode: "complete", t }); setActionForm({}); }} data-testid={`complete-task-${t.task_code}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Complete</Button>
                        )}
                        <Button size="sm" variant="ghost" className="rounded-lg" onClick={() => openHistory(t)} data-testid={`history-task-${t.task_code}`}><History className="h-3.5 w-3.5 mr-1" />History</Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between text-sm text-muted-foreground" data-testid="work-queue-pagination">
                <p>{data.total} task{data.total === 1 ? "" : "s"} · page {data.page} of {data.pages}</p>
                <div className="flex gap-2">
                  <Button variant="secondary" size="sm" className="rounded-lg" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="pagination-prev">
                    <ChevronLeft className="h-4 w-4" /> Prev
                  </Button>
                  <Button variant="secondary" size="sm" className="rounded-lg" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="pagination-next">
                    Next <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Create manual task dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-xl sm:max-w-md" data-testid="create-task-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">New manual task</DialogTitle>
            <DialogDescription>
              {isAdmin ? "Create and assign a task to any team member." : "Manual tasks you create are always assigned to you."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Task type</Label>
              <Select value={createForm.task_type} onValueChange={(v) => setCreateForm((f) => ({ ...f, task_type: v }))}>
                <SelectTrigger className="rounded-md" data-testid="create-task-type"><SelectValue placeholder="Choose type (Master Data)" /></SelectTrigger>
                <SelectContent>
                  {options.task_types.map((t) => <SelectItem key={t.value} value={t.value}>{t.value}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input value={createForm.title} onChange={(e) => setCreateForm((f) => ({ ...f, title: e.target.value }))} placeholder="What needs to be done?" className="rounded-md" data-testid="create-task-title" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Due date</Label>
                <Input type="date" value={createForm.due_date} onChange={(e) => setCreateForm((f) => ({ ...f, due_date: e.target.value }))} className="rounded-md" data-testid="create-task-due-date" />
              </div>
              <div className="space-y-1.5">
                <Label>Priority</Label>
                <Select value={createForm.priority} onValueChange={(v) => setCreateForm((f) => ({ ...f, priority: v }))}>
                  <SelectTrigger className="rounded-md" data-testid="create-task-priority"><SelectValue placeholder="Default" /></SelectTrigger>
                  <SelectContent>
                    {options.priorities.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {isAdmin && (
              <div className="space-y-1.5">
                <Label>Assign to</Label>
                <Select value={createForm.assigned_user_id} onValueChange={(v) => setCreateForm((f) => ({ ...f, assigned_user_id: v }))}>
                  <SelectTrigger className="rounded-md" data-testid="create-task-assignee"><SelectValue placeholder="Myself" /></SelectTrigger>
                  <SelectContent>
                    {options.assignees.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} ({r.role})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Details (optional)</Label>
              <Textarea value={createForm.description} onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))} rows={2} className="rounded-md" data-testid="create-task-description" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" className="rounded-lg" onClick={() => setCreateOpen(false)} data-testid="create-task-cancel">Cancel</Button>
              <Button className="rounded-lg" onClick={submitCreate} disabled={busy} data-testid="create-task-submit">
                {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Create task
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Action dialogs */}
      <Dialog open={!!action} onOpenChange={(v) => !v && setAction(null)}>
        <DialogContent className="rounded-xl sm:max-w-md" data-testid="task-action-dialog">
          {action && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {action.mode === "complete" && `Complete ${action.t.task_code}`}
                  {action.mode === "cancel" && `Cancel ${action.t.task_code}`}
                  {action.mode === "reassign" && `Reassign ${action.t.task_code}`}
                  {action.mode === "edit" && `Edit ${action.t.task_code}`}
                </DialogTitle>
                <DialogDescription>{action.t.title}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                {action.mode === "complete" && (
                  <div className="space-y-1.5">
                    <Label>Completion notes (optional)</Label>
                    <Textarea value={actionForm.notes || ""} onChange={(e) => setActionForm((f) => ({ ...f, notes: e.target.value }))} rows={3} className="rounded-md" data-testid="action-complete-notes" />
                  </div>
                )}
                {action.mode === "cancel" && (
                  <div className="space-y-1.5">
                    <Label>Cancellation reason (mandatory)</Label>
                    <Textarea value={actionForm.reason || ""} onChange={(e) => setActionForm((f) => ({ ...f, reason: e.target.value }))} rows={3} className="rounded-md" data-testid="action-cancel-reason" />
                  </div>
                )}
                {action.mode === "reassign" && (
                  <>
                    <div className="space-y-1.5">
                      <Label>New assignee</Label>
                      <Select value={actionForm.user_id || ""} onValueChange={(v) => setActionForm((f) => ({ ...f, user_id: v }))}>
                        <SelectTrigger className="rounded-md" data-testid="action-reassign-user"><SelectValue placeholder="Choose user" /></SelectTrigger>
                        <SelectContent>
                          {options.assignees.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} ({r.role})</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Remarks (optional)</Label>
                      <Input value={actionForm.remarks || ""} onChange={(e) => setActionForm((f) => ({ ...f, remarks: e.target.value }))} className="rounded-md" data-testid="action-reassign-remarks" />
                    </div>
                  </>
                )}
                {action.mode === "edit" && (
                  <>
                    <div className="space-y-1.5">
                      <Label>Title</Label>
                      <Input value={actionForm.title || ""} onChange={(e) => setActionForm((f) => ({ ...f, title: e.target.value }))} className="rounded-md" data-testid="action-edit-title" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label>Due date</Label>
                        <Input type="date" value={actionForm.due_date || ""} onChange={(e) => setActionForm((f) => ({ ...f, due_date: e.target.value }))} className="rounded-md" data-testid="action-edit-due-date" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Priority</Label>
                        <Select value={actionForm.priority || ""} onValueChange={(v) => setActionForm((f) => ({ ...f, priority: v }))}>
                          <SelectTrigger className="rounded-md" data-testid="action-edit-priority"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {options.priorities.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </>
                )}
                <div className="flex justify-end gap-2 pt-1">
                  <Button variant="ghost" className="rounded-lg" onClick={() => setAction(null)} data-testid="action-dialog-cancel">Back</Button>
                  <Button className="rounded-lg" variant={action.mode === "cancel" ? "destructive" : "default"} onClick={submitAction} disabled={busy} data-testid="action-dialog-submit">
                    {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Confirm
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!historyOf} onOpenChange={(v) => !v && setHistoryOf(null)}>
        <DialogContent className="rounded-xl sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="task-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Task history — <span className="font-mono text-base">{historyOf?.task_code}</span></DialogTitle>
            <DialogDescription>Immutable audit trail. Entries are never edited or deleted.</DialogDescription>
          </DialogHeader>
          {historyLoading ? (
            <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 rounded-lg" />)}</div>
          ) : (
            <div className="relative pl-6 space-y-4" data-testid="task-history-timeline">
              <span className="absolute left-[5px] top-2 bottom-2 w-px bg-border" />
              {history.map((e) => (
                <div key={e.id} className="relative">
                  <span className="absolute -left-6 top-1.5 h-2.5 w-2.5 rounded-full bg-primary" />
                  <div className="rounded-lg border border-border bg-card px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-mono font-medium">{e.event_type}</p>
                      <p className="text-[11px] text-muted-foreground font-mono">{formatDateTime(e.at)}</p>
                    </div>
                    {(e.previous_status || e.new_status) && (
                      <p className="text-xs text-muted-foreground mt-0.5">{e.previous_status || "—"} → {e.new_status || "—"}</p>
                    )}
                    {e.reason && <p className="text-xs mt-0.5">Reason: {e.reason}</p>}
                    {e.remarks && <p className="text-xs mt-0.5 text-muted-foreground">{e.remarks}</p>}
                    <p className="text-[11px] text-muted-foreground mt-1">by {e.actor_name}</p>
                  </div>
                </div>
              ))}
              {history.length === 0 && <p className="text-sm text-muted-foreground">No history entries.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Locking config edit dialog */}
      <Dialog open={!!cfgEdit} onOpenChange={(v) => !v && setCfgEdit(null)}>
        <DialogContent className="rounded-xl sm:max-w-md" data-testid="locking-config-dialog">
          {cfgEdit && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">Locking follow-up schedule</DialogTitle>
                <DialogDescription>{cfgEdit.company_name} — days after joining when follow-up tasks are created. Leave empty to use the default schedule.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Milestone days (comma separated)</Label>
                  <Input value={cfgEdit.value} onChange={(e) => setCfgEdit((f) => ({ ...f, value: e.target.value }))} placeholder="e.g. 20, 40, 60, 80" className="rounded-md font-mono" data-testid="locking-config-days-input" />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" className="rounded-lg" onClick={() => setCfgEdit(null)} data-testid="locking-config-cancel">Cancel</Button>
                  <Button className="rounded-lg" onClick={saveLockingCfg} disabled={busy} data-testid="locking-config-save">
                    {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />} Save schedule
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const TaskRow = ({ t, isAdmin, selected, setSelected, canManage, canEdit, onStart, onAction, onHistory }) => {
  const open = t.status === "Pending" || t.status === "In Progress";
  return (
    <TableRow className="hover:bg-muted/60" data-testid={`task-row-${t.task_code}`}>
      {isAdmin && (
        <TableCell>
          {open ? (
            <Checkbox checked={selected.includes(t.id)}
              onCheckedChange={(v) => setSelected((s) => (v ? [...s, t.id] : s.filter((id) => id !== t.id)))}
              data-testid={`select-task-${t.task_code}`} aria-label={`Select ${t.task_code}`} />
          ) : null}
        </TableCell>
      )}
      <TableCell>
        <p className="font-mono text-xs text-muted-foreground">{t.task_code}</p>
        <Badge variant="outline" className="rounded-full mt-1">{t.task_type}</Badge>
      </TableCell>
      <TableCell className="max-w-[260px]">
        <p className="text-sm font-medium truncate" title={t.title}>{t.title}</p>
        <p className="text-xs text-muted-foreground truncate">
          {[t.candidate_name, t.company_name, refCode(t)].filter(Boolean).join(" · ")}
        </p>
      </TableCell>
      <TableCell>
        <span className={`text-sm font-mono ${t.overdue ? "text-red-600 font-semibold" : ""}`}>{formatDate(t.due_date)}</span>
      </TableCell>
      <TableCell><PriorityBadge value={t.priority} /></TableCell>
      <TableCell><span className="text-xs text-muted-foreground">{t.department}</span></TableCell>
      <TableCell><span className="text-sm">{t.assigned_user_name || "—"}</span></TableCell>
      <TableCell><SourceBadge value={t.task_source} /></TableCell>
      <TableCell><StatusPill t={t} /></TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg" data-testid={`task-actions-${t.task_code}`} aria-label="Task actions">
              <Pencil className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            {t.status === "Pending" && canManage(t) && (
              <DropdownMenuItem onClick={() => onStart(t)} data-testid={`menu-start-${t.task_code}`}><Play className="h-4 w-4 mr-2" />Start</DropdownMenuItem>
            )}
            {open && canManage(t) && (
              <DropdownMenuItem onClick={() => onAction("complete")} data-testid={`menu-complete-${t.task_code}`}><CheckCircle2 className="h-4 w-4 mr-2" />Complete</DropdownMenuItem>
            )}
            {open && canEdit(t) && (
              <DropdownMenuItem onClick={() => onAction("edit")} data-testid={`menu-edit-${t.task_code}`}><Pencil className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>
            )}
            {open && isAdmin && (
              <DropdownMenuItem onClick={() => onAction("reassign")} data-testid={`menu-reassign-${t.task_code}`}><ArrowRightLeft className="h-4 w-4 mr-2" />Reassign</DropdownMenuItem>
            )}
            {open && canEdit(t) && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onAction("cancel")} className="text-destructive focus:text-destructive" data-testid={`menu-cancel-${t.task_code}`}>
                  <Ban className="h-4 w-4 mr-2" />Cancel task
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onHistory(t)} data-testid={`menu-history-${t.task_code}`}><History className="h-4 w-4 mr-2" />History</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
};

const ReportCard = ({ title, icon: Icon, children, testId }) => (
  <Card className="rounded-xl border-border shadow-none" data-testid={testId}>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-semibold flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-primary" />} {title}
      </CardTitle>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const CompliancePill = ({ c }) => (
  <div className="space-y-1 text-sm">
    <p className="text-2xl font-heading font-semibold">{c.on_time_pct != null ? `${c.on_time_pct}%` : "—"}</p>
    <p className="text-xs text-muted-foreground">on-time completion</p>
    <p className="text-xs text-muted-foreground">{c.completed}/{c.total} completed · <span className={c.overdue_open ? "text-red-600 font-medium" : ""}>{c.overdue_open} overdue open</span></p>
  </div>
);

const ReportsPanel = ({ reports, loading, onRefresh }) => {
  if (loading || !reports) {
    return <div className="grid md:grid-cols-2 gap-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-40 rounded-xl" />)}</div>;
  }
  const r = reports;
  return (
    <div className="space-y-4" data-testid="work-queue-reports-panel">
      <div className="flex justify-end">
        <Button variant="secondary" size="sm" className="rounded-lg" onClick={onRefresh} data-testid="reports-refresh-button">
          <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
        </Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <ReportCard title="Task completion" icon={CheckCircle2} testId="report-completion">
          <p className="text-2xl font-heading font-semibold">{r.completion.completion_pct}%</p>
          <p className="text-xs text-muted-foreground mt-1">{r.completion.completed} of {r.completion.total} tasks completed</p>
        </ReportCard>
        <ReportCard title="Overdue tasks" icon={AlarmClock} testId="report-overdue">
          <p className={`text-2xl font-heading font-semibold ${r.overdue.count ? "text-red-600" : ""}`}>{r.overdue.count}</p>
          <p className="text-xs text-muted-foreground mt-1">open tasks past their due date</p>
        </ReportCard>
        <ReportCard title="Avg completion time" icon={CalendarClock} testId="report-avg-completion">
          <p className="text-2xl font-heading font-semibold">{r.avg_completion.overall_hours != null ? `${r.avg_completion.overall_hours}h` : "—"}</p>
          <p className="text-xs text-muted-foreground mt-1">from creation to completion</p>
        </ReportCard>
        <ReportCard title="Task source" icon={ClipboardList} testId="report-source-split">
          <p className="text-2xl font-heading font-semibold">{r.source_split.system}<span className="text-sm text-muted-foreground font-normal"> system</span></p>
          <p className="text-xs text-muted-foreground mt-1">{r.source_split.manual} manually created</p>
        </ReportCard>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <ReportCard title="Locking compliance" icon={Lock} testId="report-locking-compliance"><CompliancePill c={r.locking_compliance} /></ReportCard>
        <ReportCard title="Interview follow-up compliance" icon={CalendarDays} testId="report-interview-compliance"><CompliancePill c={r.interview_compliance} /></ReportCard>
        <ReportCard title="Joining follow-up compliance" icon={UserRound} testId="report-joining-compliance"><CompliancePill c={r.joining_compliance} /></ReportCard>
      </div>

      <ReportCard title="Revenue at risk" icon={BadgeIndianRupee} testId="report-revenue-at-risk">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-3">
          {[["Billing reviews pending", r.revenue_at_risk.billing_ready_pending],
            ["Payment follow-ups open", r.revenue_at_risk.payment_followups_open],
            ["Payments overdue", r.revenue_at_risk.payment_followups_overdue],
            ["Locking follow-ups overdue", r.revenue_at_risk.locking_followups_overdue]].map(([label, v]) => (
            <div key={label}>
              <p className={`text-xl font-heading font-semibold ${v && label.includes("overdue") ? "text-red-600" : ""}`}>{v}</p>
              <p className="text-xs text-muted-foreground">{label}</p>
            </div>
          ))}
        </div>
        {r.revenue_at_risk.at_risk_items.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Task</TableHead><TableHead>Type</TableHead><TableHead>Candidate</TableHead><TableHead>Company</TableHead><TableHead>Due</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {r.revenue_at_risk.at_risk_items.map((i) => (
                <TableRow key={i.task_code}>
                  <TableCell className="font-mono text-xs">{i.task_code}</TableCell>
                  <TableCell className="text-xs">{i.task_type}</TableCell>
                  <TableCell className="text-xs">{i.candidate_name || "—"}</TableCell>
                  <TableCell className="text-xs">{i.company_name || "—"}</TableCell>
                  <TableCell className="text-xs font-mono">{formatDate(i.due_date)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </ReportCard>

      <div className="grid lg:grid-cols-2 gap-4">
        <ReportCard title="Recruiter workload" icon={UserRound} testId="report-recruiter-workload">
          {r.recruiter_workload.length === 0 ? <p className="text-sm text-muted-foreground">No recruiter tasks yet.</p> : (
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Recruiter</TableHead><TableHead>Open</TableHead><TableHead>In progress</TableHead><TableHead>Overdue</TableHead><TableHead>Completed</TableHead></TableRow></TableHeader>
              <TableBody>
                {r.recruiter_workload.map((w) => (
                  <TableRow key={w.recruiter_id}>
                    <TableCell className="text-sm">{w.recruiter_name}</TableCell>
                    <TableCell className="font-mono text-xs">{w.open}</TableCell>
                    <TableCell className="font-mono text-xs">{w.in_progress}</TableCell>
                    <TableCell className={`font-mono text-xs ${w.overdue ? "text-red-600 font-semibold" : ""}`}>{w.overdue}</TableCell>
                    <TableCell className="font-mono text-xs">{w.completed}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </ReportCard>
        <ReportCard title="Department workload" icon={Building2} testId="report-department-workload">
          {r.department_workload.length === 0 ? <p className="text-sm text-muted-foreground">No tasks yet.</p> : (
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Department</TableHead><TableHead>Open</TableHead><TableHead>Overdue</TableHead><TableHead>Completed</TableHead><TableHead>Total</TableHead></TableRow></TableHeader>
              <TableBody>
                {r.department_workload.map((w) => (
                  <TableRow key={w.department}>
                    <TableCell className="text-sm">{w.department}</TableCell>
                    <TableCell className="font-mono text-xs">{w.open}</TableCell>
                    <TableCell className={`font-mono text-xs ${w.overdue ? "text-red-600 font-semibold" : ""}`}>{w.overdue}</TableCell>
                    <TableCell className="font-mono text-xs">{w.completed}</TableCell>
                    <TableCell className="font-mono text-xs">{w.total}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </ReportCard>
      </div>

      <ReportCard title="Completion by task type" icon={TrendingUp} testId="report-completion-by-type">
        {r.completion.by_type.length === 0 ? <p className="text-sm text-muted-foreground">No tasks yet.</p> : (
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Task type</TableHead><TableHead>Total</TableHead><TableHead>Completed</TableHead><TableHead>Overdue open</TableHead><TableHead>Completion %</TableHead></TableRow></TableHeader>
            <TableBody>
              {r.completion.by_type.map((g) => (
                <TableRow key={g.task_type}>
                  <TableCell className="text-sm">{g.task_type}</TableCell>
                  <TableCell className="font-mono text-xs">{g.total}</TableCell>
                  <TableCell className="font-mono text-xs">{g.completed}</TableCell>
                  <TableCell className={`font-mono text-xs ${g.overdue_open ? "text-red-600 font-semibold" : ""}`}>{g.overdue_open}</TableCell>
                  <TableCell className="font-mono text-xs">{g.completion_pct}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </ReportCard>
    </div>
  );
};

const LockingSettings = ({ cfg, onEdit }) => {
  if (!cfg) {
    return <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-12 rounded-xl" />)}</div>;
  }
  return (
    <div className="space-y-4" data-testid="locking-settings-panel">
      <Card className="rounded-xl border-border shadow-none">
        <CardContent className="p-4 flex items-start gap-3">
          <span className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0"><Lock className="h-5 w-5" /></span>
          <div>
            <p className="text-sm font-medium">Default locking follow-up schedule</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Days after joining when follow-up tasks are created automatically. Default comes from Master Data
              (<span className="font-mono">locking_followup_days</span>): <span className="font-mono font-semibold" data-testid="default-locking-days">{cfg.default_days.join(", ")}</span> days.
              Companies without a custom schedule use the default.
            </p>
          </div>
        </CardContent>
      </Card>
      {cfg.companies.length === 0 ? (
        <EmptyState icon={Building2} title="No companies yet" description="Company-specific schedules appear here once companies exist in the Company Master." testId="locking-empty-state" />
      ) : (
        <Card className="rounded-xl border-border shadow-none">
          <Table data-testid="locking-config-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Company</TableHead><TableHead>Schedule</TableHead><TableHead>Effective days</TableHead><TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {cfg.companies.map((c) => (
                <TableRow key={c.company_id} className="hover:bg-muted/60" data-testid={`locking-row-${c.company_code || c.company_id}`}>
                  <TableCell>
                    <p className="text-sm font-medium">{c.company_name}</p>
                    <p className="text-xs text-muted-foreground font-mono">{c.company_code}</p>
                  </TableCell>
                  <TableCell>
                    {c.uses_default
                      ? <Badge variant="outline" className="rounded-full bg-slate-50 text-slate-600 border-slate-200">Default</Badge>
                      : <Badge variant="outline" className="rounded-full bg-primary/10 text-primary border-primary/30">Custom</Badge>}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{c.effective_days.join(", ")} days</TableCell>
                  <TableCell>
                    <Button variant="secondary" size="sm" className="rounded-lg" onClick={() => onEdit(c)} data-testid={`edit-locking-${c.company_code || c.company_id}`}>
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
};
